import { createContext, type ReactNode } from "react";

import type { Box, VisitAddress } from "@/components/ui/box.schema";

/** The page's visit host (O10). The kit never imports the host; the host provides this around the page. */
export interface BentoVisitHost {
  /**
   * Visit `address` in place of `from.box`, the box whose act was pressed; `next` steps to the next record at the same level (CASE-031).
   * `row` names the origin row when no act was pressed, as when a page restores an address: Return focuses that row's act.
   */
  visit(address: VisitAddress, from: { box: string; next?: boolean; row?: string }): void;
  /** The open visit, or null. A Bento takes it only when `slot.at` is one of its drawn boxes. */
  slot: BentoVisitSlot | null;
}

/**
 * An open visit (O10): the boxes it brought take the origin's place in the
 * column. The origin stays the same node, hidden and inert, so its search,
 * filters, selection, scroll, open rows and drafts survive to Return.
 */
export interface BentoVisitSlot {
  /** The visited address. When it changes, the kit opens `opens` and moves intent there, and keeps every other open and row state. */
  address: VisitAddress;
  /** The origin box: the visit takes its place. */
  at: string;
  /** The boxes the visit brought, in the record's order, drawn by the kit. */
  boxes: Box[];
  /** The visited boxes that start open: the box the address names. A named box that arrives later at the same address, once the address's read resolves, opens as it arrives; a box already drawn stays as the person left it. */
  opens?: string[];
  /** Wraps the drawn boxes in the host's frame: `data-visit`, the return line or the trail, the heading. */
  render(visited: ReactNode): ReactNode;
  /** A box outside the visit started its own work (it was opened, a row was opened, or a value in it changed) (O10, O7). */
  onOutside?(box: string): void;
}

export const BentoVisit = createContext<BentoVisitHost | null>(null);
